/**
 * Crimes. The board where Sallie picks work.
 *
 * Reading order: the beat bonus tells you where the money is, the hero tells you what the
 * biggest job on the board is right now, the category columns are the menu, and the right rail
 * is the cost of doing business.
 *
 * THE BUST NUMBER ON EVERY TILE IS THE SERVER'S ROLL THRESHOLD. It is computed by the Durable
 * Object from `shared/formulas.ts` against this player's Heat, district, tempo and skill, sent
 * down in basis points, and rendered here verbatim. The client applies no penalty of its own,
 * because a published curve is what makes a player push into the danger zone on purpose and a
 * number that disagrees with the roll is worse than no number at all.
 */

import { useEffect, useState } from 'react'
import type { CrimeCategory, CrimeOffer, CrimeOutcome, DistrictId, PlannerAdvice } from '@ashport/shared'
import { bailPrice, bustBase, cents, fenceFee } from '@ashport/shared/formulas'
import { A } from '../art'
import type { KnownAssetId } from '../art'
import { Icon, type IconName } from '../ui/Icon'
import { Art, Btn, Cells, Chip, Col, Panel, PanelHead, Rail, Row, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { ResultPlate, type PlateItem, type PlateRow } from '../ui/ResultPlate'
import { districts, clock, districtName, exact, heatBand, heatLabel, heatNote, useGame } from '../game/state'
import { useSession } from '../game/session'

/* ------------------------------------------------------------------ types */

type CatId = CrimeCategory
type TabId = CatId | 'all'
type Crime = CrimeOffer

interface Cat {
  id: CatId
  label: string
  icon: IconName
  c: string
  note: string
}

const CATS: Cat[] = [
  { id: 'street', label: 'Street', icon: 'fist', c: 'var(--dirty)', note: 'Fast money, small money' },
  { id: 'vice', label: 'Vice', icon: 'target', c: 'var(--nerve)', note: 'Steady weekly nut' },
  { id: 'smuggling', label: 'Smuggling', icon: 'crate', c: 'var(--clean)', note: 'Moves through the water' },
  { id: 'fraud', label: 'Fraud', icon: 'ledger', c: 'var(--gold)', note: 'Paper, not knuckles' },
  // The banner art is a period computer room with tape drives, so the category reads as Wires.
  // Nobody in 1987 Ashport says cyber.
  { id: 'cyber', label: 'Wires', icon: 'scanner', c: 'var(--police)', note: 'Telephone lines and tape' },
  { id: 'high_stakes', label: 'High Stakes', icon: 'heat', c: 'var(--arrest)', note: 'One shot, one bill' },
]

/** Success chance as the tile shows it: the exact complement of the server's bust threshold. */
const successPct = (c: Crime): number => Math.round((10_000 - c.bustBp) / 100)

const BAND_C: Record<string, string> = {
  calm: 'var(--dirty)',
  watched: 'var(--gold)',
  dangerous: 'var(--heat)',
  critical: 'var(--arrest)',
  rico: 'var(--arrest)',
}

interface Effect {
  icon: IconName
  text: string
  c: string
}

/**
 * What this Heat is actually costing, computed from the SAME formulas the server rolls and
 * prices against. Nothing here is a slogan: every line is a number the player can go and check
 * against the tile beside it.
 */
function heatEffects(heat: number, launderBp: number): Effect[] {
  const bust = Math.round(bustBase(heat) * 1000) / 10
  const fence = Math.round(fenceFee(heat) * 1000) / 10
  // bail = L * minutes_remaining * 210, in CLEAN cash. A level 10 player, thirty minutes in.
  const bail = Math.round(bailPrice(10, 30) / 100)
  return [
    {
      icon: 'target',
      text: `Base bust chance is ${bust}% at this Heat, before risk tier and the Beat`,
      c: heat >= 50 ? 'var(--arrest)' : 'var(--ink-mid)',
    },
    {
      icon: 'dirty',
      text: `Fences take ${fence}% off the book price on anything hot`,
      c: 'var(--gold)',
    },
    {
      icon: 'clean',
      text: `Laundering clears ${(launderBp / 100).toFixed(1)}% of what you put through it`,
      c: 'var(--clean)',
    },
    {
      icon: 'lock',
      text: `Bail out of a two hour hold runs about $${bail.toLocaleString('en-US')}`,
      c: 'var(--heat)',
    },
    {
      icon: 'scanner',
      text:
        heat >= 90
          ? 'A federal task force is building a case. Every action feeds it.'
          : heat >= 75
            ? 'Your name is on a board. Indictment progress is accruing.'
            : 'Heat falls one point every five minutes while you are on the street',
      c: heat >= 75 ? 'var(--arrest)' : 'var(--police)',
    },
  ]
}

/** The read on the street, by band. Twelve districts, one of them hot, and a meter. */
const POLICE_READ: Record<string, { label: string; c: string; art: KnownAssetId; prose: string }> = {
  calm: {
    label: 'Quiet',
    c: 'var(--dirty)',
    art: 'crimes.heat.increased_patrols',
    prose:
      'Normal patrol pattern. Two cars on the whole waterfront and one of them is parked. Nobody has written your name down anywhere.',
  },
  watched: {
    label: 'Watched',
    c: 'var(--gold)',
    art: 'crimes.heat.increased_patrols',
    prose:
      'Somebody took your plate off a corner. It is in a notebook rather than a file, and a notebook is still a thing that exists.',
  },
  dangerous: {
    label: 'Dangerous',
    c: 'var(--heat)',
    art: 'crimes.heat.increased_patrols',
    prose:
      'More units on patrol in your active beat. Radio traffic is up and there are checkpoints sitting on the bridge approaches.',
  },
  critical: {
    label: 'Critical',
    c: 'var(--arrest)',
    art: 'crimes.heat.sweep_in_progress',
    prose:
      'They have your name on a board with a photograph over it. Whatever you do next, somebody writes it down the same afternoon.',
  },
  rico: {
    label: 'RICO Watch',
    c: 'var(--arrest)',
    art: 'crimes.heat.sweep_in_progress',
    prose:
      'A federal task force is building a case and every move you make is another line in it. Go quiet or go to trial.',
  },
}

/* ------------------------------------------------------------- derived */

const catOf = (id: CatId): Cat => CATS.find((c) => c.id === id)!

/* --------------------------------------------------------------- pieces */

function StatGlyph({ icon, c, children }: { icon: IconName; c: string; children: string }) {
  return (
    <Row gap={3} style={{ flex: 'none' }}>
      <Icon n={icon} s={10} c={c} fill={icon === 'heat' || icon === 'energy'} />
      <span className="t-mono" style={{ fontSize: sz(10), color: c }}>
        {children}
      </span>
    </Row>
  )
}

/**
 * The Beat, live. The rotation is a pure function of the clock over a schedule published at Era
 * open, so this panel needs no polling: it recomputes from the same array every second and
 * agrees with the server exactly.
 */
function BeatPanel({ here, offers }: { here: string; offers: number }) {
  const { beat, now } = useGame()
  const d = districts().find((x) => x.id === beat.district)
  const short = d?.short ?? beat.district.toUpperCase()
  const name = districtName(beat.district)
  const youAreHere = here === beat.district
  return (
    <Panel tone="danger" style={{ width: 196, flex: 'none' }}>
      <PanelHead title="The Beat Bonus" icon="warn" accent="var(--heat)" />
      <div style={{ padding: '9px 10px 10px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div
          className="t-head"
          style={{
            fontSize: 16,
            color: 'var(--heat)',
            letterSpacing: '.11em',
            textAlign: 'center',
            padding: '6px 0',
            background: 'linear-gradient(90deg, transparent, rgba(240,90,50,.16), transparent)',
            border: '1px solid rgba(240,90,50,.3)',
          }}
        >
          {short}
        </div>
        <Row style={{ marginTop: 9, justifyContent: 'space-around', alignItems: 'stretch' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="t-label" style={{ fontSize: sz(8.5) }}>Payout Bonus</div>
            <div className="t-num" style={{ fontSize: 17, color: 'var(--dirty)' }}>
              +{Math.round(beat.payout * 100)}%
            </div>
          </div>
          <div style={{ width: 1, background: 'var(--line)' }} />
          <div style={{ textAlign: 'center' }}>
            <div className="t-label" style={{ fontSize: sz(8.5) }}>Heat Risk</div>
            <div className="t-num" style={{ fontSize: 17, color: 'var(--arrest)' }}>x{beat.heatMult}</div>
          </div>
        </Row>
        <Row gap={5} style={{ marginTop: 9, justifyContent: 'center' }}>
          <Icon n="clock" s={11} c="var(--ink-dim)" />
          <span className="t-label" style={{ fontSize: sz(8.5) }}>Ends in</span>
          <span className="t-mono" style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>
            {clock((beat.endsAt - now) / 1000)}
          </span>
        </Row>
        <div className="rule" style={{ margin: '9px 0' }} />
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          Cops are working {name} this hour. Work there and it pays 40 percent more and gains
          twice the Heat. The bonus covers your first 40 Energy in the rotation and no more.
        </p>
        <div style={{ flex: 1 }} />
        <Row gap={6} style={{ marginTop: 9 }}>
          <Icon n="pin" s={11} c="var(--brass-lit)" />
          <span className="t-label" style={{ fontSize: sz(8.5) }}>
            {youAreHere ? 'You are standing in it' : 'Jobs on this board'}
          </span>
          <div style={{ flex: 1 }} />
          <span className="t-mono" style={{ fontSize: 11, color: youAreHere ? 'var(--heat)' : 'var(--gold)' }}>
            {offers}
          </span>
        </Row>
      </div>
    </Panel>
  )
}

function Hero({
  crime,
  eff,
  runs,
  onRun,
  busy,
}: {
  crime: Crime
  eff: number
  runs: number
  onRun: () => void
  busy: boolean
}) {
  const cat = catOf(crime.cat)
  const feat = crime.cat === 'high_stakes'
  const { stacked } = useViewport()
  return (
    <Panel tone={feat ? 'danger' : 'gold'} style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      <Art
        src={A(crime.featuredId ?? `crimes.banner.${crime.cat}`, `crimes.banner.${crime.cat}`)}
        alt={`${crime.name} in ${districtName(crime.districtId)}`}
        focal="70% 45%"
        style={{ position: 'absolute', inset: 0 }}
      />
      {feat && <div className="sweep" />}
      <div
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 14px 12px',
        }}
      >
        {/* Four chips on a 377px plate: the lock chip was 80px past the edge. They wrap. */}
        <Row gap={8} style={{ flexWrap: 'wrap', rowGap: 4 }}>
          <Chip c={cat.c}>
            <Icon n={cat.icon} s={10} c={cat.c} fill={cat.icon === 'heat'} />
            {feat ? 'Featured High Stakes Crime' : `Featured ${cat.label} Crime`}
          </Chip>
          <Chip c="var(--ink-mid)">
            <Icon n="pin" s={10} c="var(--ink-mid)" />
            {districtName(crime.districtId)}
          </Chip>
          <div style={{ flex: 1 }} />
          {crime.window && (
            <Chip c="var(--gold)">
              <Icon n="clock" s={10} c="var(--gold)" />
              {crime.window}
            </Chip>
          )}
          {crime.lockedReason && (
            <Chip c="var(--arrest)">
              <Icon n="lock" s={10} c="var(--arrest)" />
              {crime.lockedReason}
            </Chip>
          )}
        </Row>

        <div style={{ flex: 1, minHeight: 6 }} />

        <div style={{ textAlign: 'center', textShadow: '0 2px 10px #000, 0 0 26px #000' }}>
          <div
            className="t-display"
            style={{ fontSize: 40, color: '#f2e6cf', letterSpacing: '.03em', lineHeight: 1 }}
          >
            {crime.name}
          </div>
          <p
            style={{
              margin: '8px auto 0',
              maxWidth: 620,
              fontSize: 12.5,
              color: 'var(--ink)',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {crime.pitch}
          </p>
        </div>

        <div style={{ flex: 1, minHeight: 6 }} />

        <Row
          gap={0}
          style={{
            // Four cells in a row beside a rail; a two-by-two grid on a stacked screen.
            display: stacked ? 'grid' : 'flex',
            gridTemplateColumns: stacked ? 'repeat(2, minmax(0, 1fr))' : undefined,
            rowGap: stacked ? 8 : undefined,
            alignSelf: stacked ? 'stretch' : 'center',
            padding: '7px 4px',
            background: 'linear-gradient(90deg, transparent, rgba(4,7,9,.86) 12%, rgba(4,7,9,.86) 88%, transparent)',
            border: '1px solid var(--line-soft)',
            borderLeft: 'none',
            borderRight: 'none',
          }}
        >
          {[
            { l: 'Payout', v: exact(crime.payout), c: 'var(--dirty)', i: 'dirty' as IconName, s: 'typical take' },
            { l: 'Heat Gain', v: `+${crime.heat}`, c: 'var(--heat)', i: 'heat' as IconName, s: 'doubles on the beat' },
            // Not "base" and not adjusted by this machine: this IS the threshold the server
            // rolls against, already carrying Heat, tempo, district and skill.
            {
              l: 'Success Chance',
              v: `${eff}%`,
              c: 'var(--gold)',
              i: 'target' as IconName,
              s: `${(crime.bustBp / 100).toFixed(1)}% they are there`,
            },
            { l: 'Energy Cost', v: String(crime.energy), c: 'var(--energy)', i: 'energy' as IconName, s: `${runs} runs left` },
          ].map((s, i) => (
            <Row key={s.l} gap={0} style={stacked ? { minWidth: 0 } : undefined}>
              {i > 0 && !stacked && <div style={{ width: 1, height: 34, background: 'var(--line)', margin: '0 4px' }} />}
              <div style={{ width: stacked ? '100%' : 132, textAlign: 'center' }}>
                <Row gap={4} style={{ justifyContent: 'center' }}>
                  <Icon n={s.i} s={10} c="var(--ink-dim)" fill={s.i === 'heat' || s.i === 'energy'} />
                  <span className="t-label" style={{ fontSize: sz(8.5) }}>{s.l}</span>
                </Row>
                <div className="t-num" style={{ fontSize: 19, color: s.c, marginTop: 1 }}>{s.v}</div>
                <div style={{ fontSize: sz(9), color: 'var(--ink-faint)' }}>{s.s}</div>
              </div>
            </Row>
          ))}
        </Row>

        <Row gap={8} style={{ justifyContent: 'center', marginTop: 10 }}>
          <Btn
            id="crime-run"
            v={feat ? 'danger' : 'gold'}
            icon="crimes"
            style={stacked ? { width: '100%' } : { minWidth: 260 }}
            disabled={busy || crime.lockedReason !== null}
            title={crime.lockedReason ?? `Spend ${crime.energy} Energy on ${crime.name}`}
            onClick={onRun}
          >
            {busy ? 'IN PROGRESS' : crime.lockedReason ? crime.lockedReason.toUpperCase() : `RUN IT  ${crime.energy}E`}
          </Btn>
        </Row>
        <Row gap={6} style={{ justifyContent: 'center', marginTop: 7 }}>
          {crime.crew && (
            <>
              <Icon n="users" s={11} c="var(--ink-faint)" />
              <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
                Crew on file: <span style={{ color: 'var(--ink)' }}>{crime.crew}</span>
              </span>
              <span style={{ color: 'var(--line)' }}>|</span>
            </>
          )}
          <Icon n="nerve" s={11} c="var(--nerve)" />
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--nerve)' }}>
            {crime.nerve} nerve
          </span>
        </Row>
      </div>
    </Panel>
  )
}

function CrimeRow({
  crime,
  eff,
  on,
  hot,
  onSelect,
  onHover,
}: {
  crime: Crime
  eff: number
  on: boolean
  hot: boolean
  onSelect: () => void
  onHover: (v: string | null) => void
}) {
  const cat = catOf(crime.cat)
  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => onHover(crime.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '5px 6px',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        background: on
          ? `linear-gradient(90deg, color-mix(in srgb, ${cat.c} 22%, transparent), transparent 78%)`
          : hot
            ? 'rgba(255,255,255,.045)'
            : 'transparent',
        border: '1px solid',
        borderColor: on ? `color-mix(in srgb, ${cat.c} 55%, transparent)` : 'transparent',
        borderBottomColor: on ? `color-mix(in srgb, ${cat.c} 55%, transparent)` : 'var(--line-soft)',
        boxShadow: on ? `inset 2px 0 0 ${cat.c}` : undefined,
        transition: 'background 130ms ease',
      }}
    >
      <Art
        src={A(crime.thumbId)}
        alt={crime.name}
        scrim="none"
        style={{ width: 38, height: 38, flex: 'none', border: '1px solid var(--line)' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Row gap={5}>
          {crime.lockedReason && <Icon n="lock" s={10} c="var(--ink-faint)" />}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 11.5,
              color: crime.lockedReason ? 'var(--ink-dim)' : on ? 'var(--ink-hi)' : 'var(--ink)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {crime.name}
          </span>
        </Row>
        {/* Locked, never hidden, and the lock states its requirement. A greyed row with no
            reason on it reads as "this game is not for you yet". */}
        {crime.lockedReason ? (
          <Row gap={7} style={{ marginTop: 2, flexWrap: 'wrap', rowGap: 2 }}>
            <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--gold)' }}>
              {crime.lockedReason}
            </span>
            <StatGlyph icon="dirty" c="var(--ink-faint)">{exact(crime.payout)}</StatGlyph>
          </Row>
        ) : (
          <Row gap={7} style={{ marginTop: 2 }}>
            <StatGlyph icon="dirty" c="var(--dirty)">{exact(crime.payout)}</StatGlyph>
            <StatGlyph icon="heat" c="var(--heat)">{`+${crime.heat}`}</StatGlyph>
            <StatGlyph icon="target" c="var(--gold)">{`${eff}%`}</StatGlyph>
            <StatGlyph icon="energy" c="var(--energy)">{String(crime.energy)}</StatGlyph>
          </Row>
        )}
      </div>
    </button>
  )
}

function CatColumn({
  cat,
  rows,
  sel,
  hov,
  setSel,
  setHov,
}: {
  cat: Cat
  rows: Crime[]
  sel: string
  hov: string | null
  setSel: (v: string) => void
  setHov: (v: string | null) => void
}) {
  const hi = cat.id === 'high_stakes'
  return (
    <Panel tone={hi ? 'danger' : undefined} style={{ minWidth: 0 }}>
      <PanelHead
        title={cat.label}
        icon={cat.icon}
        accent={cat.c}
        right={
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
            {rows.length}
          </span>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '4px 5px 6px' }}>
        <div className="t-label" style={{ fontSize: sz(8), color: cat.c, padding: '2px 3px 5px' }}>
          {cat.note}
        </div>
        {rows.map((c) => (
          <CrimeRow
            key={c.id}
            crime={c}
            eff={successPct(c)}
            on={sel === c.id}
            hot={hov === c.id}
            onSelect={() => setSel(c.id)}
            onHover={setHov}
          />
        ))}
      </div>
    </Panel>
  )
}

function DetailTable({
  rows,
  sel,
  setSel,
}: {
  rows: Crime[]
  sel: string
  setSel: (v: string) => void
}) {
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title={`${catOf(rows[0].cat).label} Board`}
        icon={catOf(rows[0].cat).icon}
        accent={catOf(rows[0].cat).c}
        sub={catOf(rows[0].cat).note}
        right={
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
            {rows.length} jobs on the board
          </span>
        }
      />
      <div className="scroll-y tbl-wrap" style={{ flex: 1 }}>
        <table className="grid-tbl">
          <thead>
            <tr>
              <th style={{ width: 54 }} />
              <th>Crime</th>
              <th>District</th>
              <th style={{ textAlign: 'right' }}>Level</th>
              <th style={{ textAlign: 'right' }}>Payout</th>
              <th style={{ textAlign: 'right' }}>Heat</th>
              <th style={{ textAlign: 'right' }}>Success</th>
              <th style={{ textAlign: 'right' }}>Energy</th>
              <th style={{ textAlign: 'right' }}>Nerve</th>
              <th style={{ width: 84 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} data-on={sel === c.id || undefined} onClick={() => setSel(c.id)} style={{ cursor: 'pointer' }}>
                <td>
                  <Art
                    src={A(c.thumbId)}
                    alt={c.name}
                    scrim="none"
                    style={{ width: 40, height: 32, border: '1px solid var(--line)' }}
                  />
                </td>
                <td>
                  <div style={{ color: 'var(--ink-hi)', fontSize: 12 }}>{c.name}</div>
                  <div
                    style={{
                      fontSize: sz(10.5),
                      color: 'var(--ink-dim)',
                      maxWidth: 460,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.pitch}
                  </div>
                </td>
                <td style={{ color: 'var(--ink-mid)', whiteSpace: 'nowrap' }}>{districtName(c.districtId)}</td>
                <td className="t-mono" style={{ textAlign: 'right', color: c.lockedReason ? 'var(--arrest)' : 'var(--ink-dim)' }}>
                  {c.lockedReason ?? 'open'}
                </td>
                <td className="t-num" style={{ textAlign: 'right', color: 'var(--dirty)' }}>{exact(c.payout)}</td>
                <td className="t-num" style={{ textAlign: 'right', color: 'var(--heat)' }}>+{c.heat}</td>
                <td className="t-num" style={{ textAlign: 'right', color: 'var(--gold)' }}>{successPct(c)}%</td>
                <td className="t-num" style={{ textAlign: 'right', color: 'var(--energy)' }}>{c.energy}</td>
                <td className="t-num" style={{ textAlign: 'right', color: 'var(--nerve)' }}>{c.nerve}</td>
                <td>
                  <Btn size="sm" v={sel === c.id ? 'gold' : 'ghost'}>{sel === c.id ? 'Shown' : 'Show'}</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function HeatPanel({ heat, launderBp }: { heat: number; launderBp: number }) {
  const band = heatBand(heat)
  const effects = heatEffects(heat, launderBp)
  return (
    <Panel tone="danger" style={{ flex: 'none' }}>
      <PanelHead title="Heat Meter" icon="heat" accent="var(--heat)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <Row gap={9}>
          <Icon n="heat" s={20} c="var(--heat)" fill />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Cells v={heat} crit={heat >= 75} />
          </div>
          <div className="t-num" style={{ fontSize: 22, color: BAND_C[band] }}>{heat}</div>
        </Row>
        <div className="t-head" style={{ fontSize: 13, color: BAND_C[band], marginTop: 8, letterSpacing: '.14em' }}>
          {heatLabel[band]}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-mid)' }}>{heatNote[band]}</div>
        <div className="rule" style={{ margin: '9px 0 8px' }} />
        <div className="t-label" style={{ fontSize: sz(8.5), marginBottom: 5 }}>Heat Effects</div>
        <Col gap={4}>
          {effects.map((e) => (
            <Row key={e.text} gap={7} style={{ alignItems: 'flex-start' }}>
              <Icon n={e.icon} s={12} c={e.c} style={{ marginTop: 2 }} />
              <span style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.35 }}>{e.text}</span>
            </Row>
          ))}
        </Col>
      </div>
    </Panel>
  )
}

function PolicePanel({ heat, hot, here }: { heat: number; hot: string; here: string }) {
  const read = POLICE_READ[heatBand(heat)]
  const onTheBeat = hot === here
  return (
    <Panel tone="police" style={{ flex: 'none', overflow: 'hidden' }}>
      <PanelHead title="Police Attention" icon="scanner" accent="var(--police)" />
      <Art
        src={A(read.art, 'crimes.heat.sweep_in_progress')}
        alt="Patrol car idling under the elevated"
        scrim="soft"
        style={{ height: 74, flex: 'none' }}
      >
        <div style={{ position: 'absolute', inset: 'auto 10px 6px' }}>
          <div className="t-head" style={{ fontSize: 15, color: read.c, letterSpacing: '.16em' }}>
            {read.label}
          </div>
        </div>
        {heat >= 50 && <div className="sweep" />}
      </Art>
      <div style={{ padding: '8px 11px 10px' }}>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.45 }}>{read.prose}</p>
        <Col gap={3} style={{ marginTop: 8 }}>
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>Beat is running</span>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--police)' }}>
              {districtName(hot)}
            </span>
          </Row>
          {/*
            * THE MOVE, which no control anywhere in the game offered.
            *
            * `api.setDistrict` existed with zero callers and there was no store action at all, so
            * where a player stood was fixed at signup for the whole Era. It decides two live
            * numbers: `crimePayout` pays 0.9x outside a crime's home district, and the Beat is
            * x1.35 on bust odds where it is running against x0.85 everywhere else. Reading both of
            * those on this panel and being unable to act on either is the panel telling a player
            * about a decision they are not allowed to make.
            *
            * A select rather than a map, here, because this panel is the numbers and the map is
            * the Territory screen's job.
            */}
          <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>You are standing in</span>
            <Move here={here} hot={hot} />
          </Row>
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>Bust odds where you stand</span>
            <span className="t-mono" style={{ fontSize: 11, color: read.c }}>
              {onTheBeat ? 'x1.35' : 'x0.85'}
            </span>
          </Row>
        </Col>
      </div>
    </Panel>
  )
}

/**
 * Where the player stands, and the only control that changes it.
 *
 * Free and instant on the server: `POST /api/me/district` validates the id and writes. The cost is
 * entirely in what it does to the two multipliers above, which is why it sits between them.
 */
function Move({ here, hot }: { here: string; hot: string }) {
  const busy = useSession((s) => s.busy)
  const setDistrict = useSession((s) => s.setDistrict)
  return (
    <select
      value={here}
      disabled={busy}
      onChange={(e) => void setDistrict(e.target.value as DistrictId)}
      aria-label="Move to another district"
      style={{
        background: '#05080a',
        border: '1px solid var(--line)',
        color: here === hot ? 'var(--heat)' : 'var(--ink-mid)',
        font: '400 11px/1.2 var(--f-mono, var(--f-body))',
        padding: '2px 5px',
        outline: 'none',
        cursor: busy ? 'default' : 'pointer',
      }}
    >
      {districts().map((d) => (
        <option key={d.id} value={d.id}>
          {d.name}
          {d.id === hot ? ' (the Beat)' : ''}
        </option>
      ))}
    </select>
  )
}

/**
 * One card of the Planner. Everything on it came from the server, which is what guarantees it
 * points at something this player can actually do right now. See `server/src/game/planner.ts`.
 */
function AdviceCard({ a, onGo }: { a: PlannerAdvice; onGo: () => void }) {
  const c =
    a.tone === 'safe' ? 'var(--dirty)' : a.tone === 'greedy' ? 'var(--arrest)' : 'var(--gold)'
  const icon: IconName =
    a.kind === 'job' ? 'jobs' : a.kind === 'crime' ? 'crimes' : a.kind === 'travel' ? 'pin' : 'clock'
  return (
    <div style={{ border: `1px solid color-mix(in srgb, ${c} 38%, transparent)` }}>
      <Row
        gap={6}
        style={{
          padding: '4px 8px',
          background: `color-mix(in srgb, ${c} 16%, transparent)`,
          borderBottom: `1px solid color-mix(in srgb, ${c} 30%, transparent)`,
        }}
      >
        <Icon n={icon} s={11} c={c} />
        <span className="t-head" style={{ fontSize: sz(10), color: c, letterSpacing: '.14em' }}>
          {a.label}
        </span>
      </Row>
      <div style={{ padding: '7px 8px 8px' }}>
        <div className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.06em' }}>
          {a.title}
        </div>
        <p style={{ margin: '5px 0 0', fontSize: 11, lineHeight: 1.45, color: 'var(--ink-mid)' }}>
          {a.body}
        </p>
        {a.kind === 'crime' && a.refId && (
          <Btn v={a.tone === 'greedy' ? 'danger' : 'gold'} size="sm" full style={{ marginTop: 7 }} onClick={onGo}>
            Show it
          </Btn>
        )}
      </div>
    </div>
  )
}

function LegendBar() {
  const items: { i: IconName; c: string; t: string }[] = [
    { i: 'dirty', c: 'var(--dirty)', t: 'Payout (dirty cash)' },
    { i: 'heat', c: 'var(--heat)', t: 'Heat points gained on success' },
    { i: 'target', c: 'var(--gold)', t: 'Success chance, the number rolled against' },
    { i: 'energy', c: 'var(--energy)', t: 'Energy cost per attempt' },
  ]
  return (
    <Row
      gap={22}
      style={{
        flex: 'none',
        justifyContent: 'center',
        flexWrap: 'wrap',
        rowGap: 4,
        padding: '6px 10px',
        borderTop: '1px solid var(--line-soft)',
        background: 'rgba(255,255,255,.015)',
      }}
    >
      {items.map((x) => (
        <Row key={x.t} gap={6}>
          <Icon n={x.i} s={12} c={x.c} fill={x.i === 'heat' || x.i === 'energy'} />
          <span className="t-label" style={{ fontSize: sz(8.5) }}>{x.t}</span>
        </Row>
      ))}
    </Row>
  )
}

/**
 * The result. Nothing on this card was computed here.
 *
 * A bust states the number it rolled against, in words, because a first significant loss retains
 * a player only when the game explains it. "You were at Sixty-eight. That was a two in five. It
 * came up." is the difference between a tradeoff a player trusts and a game they think cheated.
 */
function Outcome({ o, onClose }: { o: CrimeOutcome; onClose: () => void }) {
  /*
   * THE DROPS, ON SCREEN. `CrimeOutcome.drops` and `seized` have been on the wire since Phase 2
   * and nothing read either: a player handed a Rare saw a paragraph and a Heat number. The
   * catalogue turns an id into a name and a picture; it is the same 54 KB read the Market makes,
   * fetched once per manifest version and only when there is something to name.
   */
  const catalog = useSession((s) => s.catalog)
  const loadCatalog = useSession((s) => s.loadCatalog)
  const named = (o.drops?.length ?? 0) + (o.seized?.length ?? 0)
  useEffect(() => {
    if (named > 0) void loadCatalog().catch(() => undefined)
  }, [named, loadCatalog])
  const bad = o.jailUntil !== null || o.dirtyLost > 0 || o.kind === 'police_bust'
  const name = (id: string) => catalog.get(id)?.name ?? id
  const art = (id: string) => catalog.get(id)?.artId ?? null
  const items: PlateItem[] = [
    ...(o.drops ?? []).map((d) => ({ name: name(d.itemId), qty: d.qty, artId: art(d.itemId), kind: 'drop' as const })),
    ...(o.seized ?? []).map((d) => ({ name: name(d.itemId), qty: d.qty, artId: art(d.itemId), kind: 'seized' as const })),
  ]
  const rows: PlateRow[] = []
  if (o.dirtyGained > 0)
    rows.push({ icon: 'dirty', label: 'Dirty cash', value: o.dirtyGained, fmt: (n) => `+${exact(cents(n))}`, c: 'var(--dirty)', fill: true })
  if (o.dirtyLost > 0)
    rows.push({ icon: 'dirty', label: 'Confiscated', value: o.dirtyLost, fmt: (n) => `-${exact(cents(n))}`, c: 'var(--arrest)', fill: true })
  rows.push({
    icon: 'heat',
    label: 'Heat',
    value: o.heatDelta,
    fmt: (n) => `${n > 0 ? '+' : ''}${n}`,
    c: o.heatDelta > 0 ? 'var(--heat)' : 'var(--dirty)',
    fill: true,
  })
  if (o.xpGained > 0) rows.push({ icon: 'rep', label: 'Experience', value: o.xpGained, fmt: (n) => `+${n}`, c: 'var(--gold)' })
  return (
    <ResultPlate
      tone={bad ? 'danger' : 'safe'}
      title={bad ? 'They Were Already There' : 'Clean'}
      artId={o.artId}
      fallbackArtId="crimes.result.clean_success"
      headline={o.headline}
      body={o.body}
      rows={rows}
      items={items}
      level={{ before: o.levelBefore, after: o.levelAfter, energyMax: o.energyMaxAfter }}
      next={o.jailUntil !== null ? 'You are in a holding cell. Post bail on the Home screen or sit it out.' : null}
      onClose={onClose}
    />
  )
}

/** A refusal, in voice, on the screen that caused it. Never a status code. */
function Refusal({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <Panel tone="danger">
      <PanelHead
        title="No"
        icon="warn"
        accent="var(--arrest)"
        right={
          <button
            onClick={onClose}
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            aria-label="Dismiss"
          >
            <Icon n="close" s={11} c="var(--ink-dim)" />
          </button>
        }
      />
      <p style={{ margin: 0, padding: '9px 11px 11px', fontSize: 12, color: 'var(--ink)' }}>{text}</p>
    </Panel>
  )
}

/* ------------------------------------------------------------------ screen */

export function Crimes() {
  const { player } = useGame()
  const [tab, setTab] = useState<TabId>('all')
  const [sel, setSel] = useState<string | null>(null)
  const [hov, setHov] = useState<string | null>(null)
  const {
    me,
    crimes: board,
    loadCrimes,
    commitCrime,
    busy,
    lastCrime,
    refusal,
    clearOutcome,
    clearRefusal,
  } = useSession()

  // The read that renders the buttons is the read that mints their token.
  useEffect(() => {
    void loadCrimes()
  }, [loadCrimes])
  // Above the early return, like every other hook: a hook below it is a hook that is called on
  // some renders and not others, and React tears the screen down the moment the board loads.
  const { stacked } = useViewport()

  const CRIMES = board?.offers ?? []
  if (CRIMES.length === 0) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--ink-faint)' }}>
        <span className="t-head" style={{ letterSpacing: '.24em', fontSize: 11 }}>
          READING THE BOARD
        </span>
      </div>
    )
  }

  const band = heatBand(player.heat)
  // The Planner is the SERVER'S, because only the server knows the whole board. The two ends
  // it used to be computed from live in `server/src/game/planner.ts` now.
  const advice = board?.advice ?? []
  // The hero opens on the biggest thing the player can actually run. Locked tiles stay VISIBLE
  // per GAME-DESIGN, but they are not what the screen opens on.
  const open = CRIMES.filter((c) => c.lockedReason === null)
  const pool = open.length > 0 ? open : CRIMES
  const big = pool.reduce((a, b) => (b.payout > a.payout ? b : a))

  const crime = CRIMES.find((c) => c.id === sel) ?? big
  const runs = Math.floor(player.energy / crime.energy)

  const present = new Set(CRIMES.map((c) => c.cat))
  const tabs: { id: TabId; label: string }[] = [
    { id: 'all', label: 'All Crimes' },
    ...CATS.filter((c) => present.has(c.id)).map((c) => ({ id: c.id as TabId, label: c.label })),
  ]

  return (
    <Stack>
      {/* centre */}
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        {/* Stacked, what just happened goes ABOVE the board, where the thumb that caused it is. */}
        {stacked && refusal && <Refusal text={refusal} onClose={clearRefusal} />}
        {stacked && lastCrime && <Outcome o={lastCrime} onClose={clearOutcome} />}
        <Row gap={8} className="cols" style={{ height: 316, flex: 'none', alignItems: 'stretch' }}>
          <BeatPanel here={board?.districtId ?? ''} offers={CRIMES.length} />
          <Col gap={5} style={{ flex: 1, minWidth: 0 }}>
            <Row gap={7} style={{ flex: 'none', padding: '0 2px', flexWrap: 'wrap' }}>
              <Icon n="eye" s={13} c={BAND_C[band]} className={band === 'calm' ? undefined : 'pulse'} />
              <span className="t-head" style={{ fontSize: sz(10.5), color: BAND_C[band], letterSpacing: '.16em' }}>
                Police Attention: {heatLabel[band]}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>{heatNote[band]}</span>
              <div style={{ flex: 1 }} />
              <span className="t-mono" style={{ fontSize: sz(10.5), color: BAND_C[band] }}>
                {player.heat} / 100
              </span>
            </Row>
            <Hero
              crime={crime}
              eff={successPct(crime)}
              runs={runs}
              busy={busy}
              onRun={() => void commitCrime(crime.id)}
            />
          </Col>
        </Row>

        <Panel style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Row style={{ flex: 'none', padding: '0 6px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs items={tabs} value={tab} onChange={setTab} />
            </div>
            <Row gap={7} style={{ paddingRight: 6, flexWrap: 'wrap', padding: '4px 6px' }}>
              <Icon n="filter" s={11} c="var(--ink-faint)" />
              <span className="t-label" style={{ fontSize: sz(8.5) }}>
                {CRIMES.length} jobs available
              </span>
              <span style={{ color: 'var(--line)' }}>|</span>
              <Icon n="energy" s={11} c="var(--energy)" fill />
              <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--energy)' }}>
                {player.energy} / {player.energyMax}
              </span>
              <span style={{ color: 'var(--line)' }}>|</span>
              <Icon n="target" s={11} c="var(--arrest)" />
              <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--arrest)' }}>
                {(crime.bustBp / 100).toFixed(1)}% on this one
              </span>
            </Row>
          </Row>

          <div style={{ flex: 1, minHeight: 0, padding: 8 }}>
            {tab === 'all' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: stacked ? '1fr' : `repeat(${Math.max(1, tabs.length - 1)}, minmax(0, 1fr))`,
                  gap: 8,
                  height: stacked ? undefined : '100%',
                }}
              >
                {CATS.filter((c) => present.has(c.id)).map((c) => (
                  <CatColumn
                    key={c.id}
                    cat={c}
                    rows={CRIMES.filter((x) => x.cat === c.id)}
                    sel={sel ?? crime.id}
                    hov={hov}
                    setSel={setSel}
                    setHov={setHov}
                  />
                ))}
              </div>
            ) : (
              <div style={{ height: '100%', display: 'flex', minHeight: 0 }}>
                <DetailTable rows={CRIMES.filter((x) => x.cat === tab)} sel={sel ?? crime.id} setSel={setSel} />
              </div>
            )}
          </div>

          <LegendBar />
        </Panel>
      </Col>

      {/* right rail */}
      <Rail width={300}>
        {!stacked && refusal && <Refusal text={refusal} onClose={clearRefusal} />}
        {!stacked && lastCrime && <Outcome o={lastCrime} onClose={clearOutcome} />}
        <HeatPanel heat={player.heat} launderBp={me?.launderRateBp ?? 0} />
        <PolicePanel heat={player.heat} hot={board!.beat.district} here={board!.districtId} />
        <Panel tone="gold" style={{ flex: 1, minHeight: 0 }}>
          <PanelHead title="Planner: What Should I Do?" icon="help" accent="var(--gold)" />
          <div className="scroll-y" style={{ flex: 1, padding: '9px 10px 10px' }}>
            {/* The server's advice, which is never empty and never points at a locked tile. */}
            <Col gap={8}>
              {advice.map((a, n) => (
                <AdviceCard
                  key={`${a.kind}-${a.refId ?? n}`}
                  a={a}
                  onGo={() => {
                    if (a.refId) setSel(a.refId)
                  }}
                />
              ))}
            </Col>

          </div>
        </Panel>
      </Rail>
    </Stack>
  )
}
